import { useState } from 'react'

import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/toastContext'
import { useCandidateTaskAssignees, useCreateCandidateTask } from '@/features/recruiter/hooks/useCandidates'
import type { CandidateTaskType } from '@/features/recruiter/api/candidates.api'

interface ReminderTaskModalProps {
  show: boolean
  onClose: () => void
  studentProfileId: number | null
  candidateName?: string
}

const TASK_TYPE_OPTIONS: { value: CandidateTaskType; label: string }[] = [
  { value: 'call_later', label: 'Call later' },
  { value: 'interview_follow_up', label: 'Interview follow up' },
  { value: 'send_jd', label: 'Send JD' },
  { value: 'other', label: 'Other task' },
]

export function ReminderTaskModal({ show, onClose, studentProfileId, candidateName }: ReminderTaskModalProps) {
  const [type, setType] = useState<CandidateTaskType>('call_later')
  const [dueAt, setDueAt] = useState('')
  const [note, setNote] = useState('')
  const [assigneeId, setAssigneeId] = useState<string>('')

  // Reset the form during render when a new candidate is targeted, instead
  // of remounting the Modal on a key change — remounting would tear down
  // the Bootstrap Modal instance mid hide-transition and throw (bootstrap.js
  // reads a now-null backdrop element from its pending transitionend
  // handler). This is React's supported "adjust state while rendering"
  // pattern, not an effect, so it doesn't trigger extra renders.
  const [lastStudentProfileId, setLastStudentProfileId] = useState(studentProfileId)
  if (studentProfileId !== lastStudentProfileId) {
    setLastStudentProfileId(studentProfileId)
    setType('call_later')
    setDueAt('')
    setNote('')
    setAssigneeId('')
  }

  const { data: assignees } = useCandidateTaskAssignees()
  const createTask = useCreateCandidateTask()
  const toast = useToast()

  const handleSubmit = () => {
    if (!studentProfileId) return

    createTask.mutate(
      {
        studentProfileId,
        type,
        note: note.trim() || undefined,
        // The datetime-local input's value has no timezone marker (e.g.
        // "2026-08-08T20:00") and represents the browser's local wall-clock
        // time. Sending that string as-is gets parsed by Carbon as UTC
        // (config('app.timezone') is UTC), silently shifting the reminder
        // by the user's UTC offset. `new Date(...)` on a timezone-less
        // date-time string is specified to parse as local time, so
        // `.toISOString()` here converts it to the correct UTC instant.
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        assigned_to_user_id: assigneeId ? Number(assigneeId) : undefined,
      },
      {
        onSuccess: () => {
          setType('call_later')
          setDueAt('')
          setNote('')
          setAssigneeId('')
          toast.success(candidateName ? `Reminder set for ${candidateName}.` : 'Reminder set.')
          onClose()
        },
        onError: () => toast.error('Could not set reminder. Please try again.'),
      },
    )
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={candidateName ? `Set reminder — ${candidateName}` : 'Set reminder'}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={createTask.isPending || !studentProfileId}
            onClick={handleSubmit}
          >
            {createTask.isPending ? 'Saving…' : 'Set reminder'}
          </button>
        </>
      }
    >
      <div className="mb-3">
        <label className="form-label" htmlFor="reminder-task-type">
          Task type
        </label>
        <select
          id="reminder-task-type"
          className="form-select"
          value={type}
          onChange={(e) => setType(e.target.value as CandidateTaskType)}
        >
          {TASK_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label" htmlFor="reminder-due-at">
          Due date
        </label>
        <input
          id="reminder-due-at"
          type="datetime-local"
          className="form-control"
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label" htmlFor="reminder-assignee">
          Assign to
        </label>
        <select id="reminder-assignee" className="form-select" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
          <option value="">Myself</option>
          {assignees?.map((assignee) => (
            <option key={assignee.id} value={assignee.id}>
              {assignee.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-1">
        <label className="form-label" htmlFor="reminder-note">
          Note
        </label>
        <textarea
          id="reminder-note"
          className="form-control"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note for this reminder"
        />
      </div>

      {createTask.isError && <p className="text-danger fs-12 mt-2 mb-0">Could not set reminder. Please try again.</p>}
    </Modal>
  )
}
